export type HomeworkTransport = 'student' | 'teacher';

const mockWriteQueues = new Map<string, Promise<void>>();

// Only product-owned origins are allowed. A ticket or URL parameter can never
// choose a destination for the bearer credential.
export function teacherPracticeOrigin() {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return `${window.location.protocol}//${host}:6126`;
  if (host === 'dauyan.net' || host.endsWith('.dauyan.net')) return 'https://org.dauyan.net';
  if (host === 'dauyan.com' || host.endsWith('.dauyan.com')) return 'https://org.dauyan.com';
  throw new Error('当前站点尚未配置教师体验入口。');
}

export async function teacherPracticePost<T>(action: string, body: object = {}, token?: string): Promise<{ code: number; data: T; msg: string; }> {
  if (!/^(exchange|content|state|progress|result|audio\/(upload|recognize)|assessment\/[a-z-]+|mock\/[a-z-]+)$/.test(action)) {
    throw new Error('教师体验操作无效。');
  }
  const send = () => sendTeacherPracticeRequest<T>(action, body, token);
  if (!token || !/^mock\/(?:save-|finished-)/.test(action)) return send();

  // Duration tracking, draft autosave and submission share one teacher CAS
  // revision. Keep their original order without replaying a stale draft.
  const request = (mockWriteQueues.get(token) || Promise.resolve()).then(send);
  const settled = request.then(() => undefined, () => undefined);
  mockWriteQueues.set(token, settled);
  void settled.then(() => {
    if (mockWriteQueues.get(token) === settled) mockWriteQueues.delete(token);
  });
  return request;
}

async function sendTeacherPracticeRequest<T>(action: string, body: object, token?: string): Promise<{ code: number; data: T; msg: string; }> {
  const multipart = body instanceof FormData;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 60_000);
  try {
    const response = await fetch(`${teacherPracticeOrigin()}/api/workspace/practice/teacher/${action}`, {
      method: 'POST', credentials: 'omit', cache: 'no-store', redirect: 'error', signal: controller.signal,
      headers: { ...(!multipart ? { 'Content-Type': 'application/json' } : {}), ...(token ? { 'X-Homework-Session': token } : {}) },
      body: multipart ? body : JSON.stringify(body),
    });
    const payload = await response.json();
    if (!response.ok || payload.code !== 200) throw new Error(payload.detail || payload.msg || '教师体验请求未完成，请重试。');
    return payload;
  } finally { window.clearTimeout(timeout); }
}
